import React, { useState, useEffect, useMemo } from 'react';
import {
  ArrowLeft, Plus, TrendingUp, TrendingDown, DollarSign,
  Search, RefreshCw, Edit2, Trash2, X, CheckCircle, AlertCircle,
  Calendar, Shield, Clock, Wrench,
} from 'lucide-react';
import api from '../services/api';
import './Financeiro.css';
import { parseOSTarefa } from '../utils/osTarefa';

/* ─── Formatadores ────────────────────────────────────────────── */
const formatCurrency = (value) =>
  new Intl.NumberFormat('pt-BR', { style: 'currency', currency: 'BRL' }).format(value || 0);

const extractDatePart = (raw) => {
  if (!raw) return null;
  const part = String(raw).split(/[T ]/)[0];
  return /^\d{4}-\d{2}-\d{2}$/.test(part) ? part : null;
};

const parseDateForCard = (dateString) => {
  const datePart = extractDatePart(dateString);
  if (!datePart) return { day: '--', month: '---' };
  const date = new Date(`${datePart}T12:00:00`);
  if (isNaN(date.getTime())) return { day: '--', month: '---' };
  return {
    day:   String(date.getDate()).padStart(2, '0'),
    month: date.toLocaleString('pt-BR', { month: 'short' }).replace('.', '').toUpperCase(),
  };
};

/* ─── Status de Conciliação ───────────────────────────────────────
 *
 *  O backend retorna 3 situações possíveis para cada lançamento:
 *
 *  'conciliado'  → conciliado = 1 (via OFX ou baixa direta confirmada)
 *  'confirmado'  → conciliado = 0, mas tem conta paga/recebida vinculada
 *                  (usuário apontou o pagamento — sistema confirmou,
 *                   extrato bancário ainda não importado)
 *  'pendente'    → conciliado = 0, sem nenhuma origem identificada
 *                  (lançamento manual flutuante — precisa atenção real)
 *
 *  Prioridade de leitura:
 *  1. status_conciliacao (campo calculado se backend /financeiro for patchado)
 *  2. conciliado === 1
 *  3. despesa_id ou os_id preenchidos (origem identificada no próprio registro)
 * ──────────────────────────────────────────────────────────────── */
const getConcilStatus = (tx) => {
  // Campo calculado pelo backend patchado (GET /financeiro com JOIN)
  if (tx.status_conciliacao) return tx.status_conciliacao;

  // Fallback: campo conciliado direto da tabela
  const conciliado =
    tx.conciliado === 1 ||
    tx.conciliado === '1' ||
    tx.conciliado === true;

  if (conciliado) return 'conciliado';

  // Tem despesa_id ou os_id = foi gerado por uma ação do usuário
  if (tx.despesa_id || tx.os_id) return 'confirmado';

  // Lançamento manual sem origem = floating real
  return 'pendente';
};

/* ─── Badge de conciliação ────────────────────────────────────── */
const ConcilBadge = ({ tx }) => {
  const status = getConcilStatus(tx);

  if (status === 'conciliado') {
    return (
      <span className="ex-fin-badge ex-fin-badge-success" title="Verificado via extrato bancário (OFX)">
        <CheckCircle size={11} /> Conciliado
      </span>
    );
  }

  if (status === 'confirmado') {
    return (
      <span className="ex-fin-badge ex-fin-badge-info" title="Pagamento confirmado pelo sistema — extrato OFX ainda não importado">
        <Shield size={11} /> Confirmado
      </span>
    );
  }

  // pendente = floating real
  return (
    <span className="ex-fin-badge ex-fin-badge-warning" title="Lançamento sem origem identificada — requer conciliação manual">
      <Clock size={11} /> Ag. Conciliação
    </span>
  );
};

/* ─── Badge de OS-Tarefa ──────────────────────────────────────────
 *  Detectado pelo prefixo "[OS-Tarefa #...]" na descrição — gravado pelo
 *  lancarDespesaOSTarefaFechada e propagado para o financeiro quando a
 *  conta a pagar correspondente é baixada.
 * ──────────────────────────────────────────────────────────────── */
const TarefaBadge = ({ tx }) => {
  const { isTarefa, osId } = parseOSTarefa(tx.descricao);
  if (!isTarefa) return null;
  return (
    <span className="ex-fin-badge ex-fin-badge-tarefa" title={`Origem: OS de Tarefa #${osId}`}>
      <Wrench size={11} /> Tarefa OS
    </span>
  );
};

/* ─── Componente Principal ────────────────────────────────────── */
const ExtratoFinanceiro = () => {
  const [transactions, setTransactions] = useState([]);
  const [isLoading,    setIsLoading]    = useState(false);
  const [filters, setFilters] = useState({
    search:     '',
    dataInicio: new Date(new Date().setDate(1)).toISOString().split('T')[0],
    dataFim:    new Date(new Date().getFullYear(), new Date().getMonth() + 1, 0)
                  .toISOString().split('T')[0],
  });

  const [isModalOpen, setIsModalOpen] = useState(false);
  const [modalMode,   setModalMode]   = useState('create');
  const [formData, setFormData] = useState({
    id: null, tipo: 'Despesa', descricao: '',
    valor: '', data: new Date().toISOString().split('T')[0], categoria_id: '',
  });

  /* ── Fetch ── */
  const fetchTransactions = async () => {
    setIsLoading(true);
    try {
      const params = new URLSearchParams();
      if (filters.dataInicio) params.append('dataInicio', filters.dataInicio);
      if (filters.dataFim)    params.append('dataFim',    filters.dataFim);
      const response = await api.get(`/financeiro?${params.toString()}`);
      setTransactions(response.data);
    } catch (error) {
      console.error('Erro ao buscar transações:', error);
      alert('Falha ao carregar o extrato financeiro.');
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => { fetchTransactions(); }, [filters.dataInicio, filters.dataFim]);

  /* ── Filtro local ── */
  const filteredTransactions = useMemo(() =>
    transactions.filter(t =>
      t.descricao?.toLowerCase().includes(filters.search.toLowerCase()) ||
      (t.categoria_nome && t.categoria_nome.toLowerCase().includes(filters.search.toLowerCase()))
    ),
  [transactions, filters.search]);

  /* ── KPIs ── */
  const kpis = useMemo(() =>
    filteredTransactions.reduce((acc, curr) => {
      const val = parseFloat(curr.valor);
      if (curr.tipo === 'Receita' || val > 0) acc.receitas += Math.abs(val);
      else                                     acc.despesas += Math.abs(val);
      acc.saldo = acc.receitas - acc.despesas;
      return acc;
    }, { receitas: 0, despesas: 0, saldo: 0 }),
  [filteredTransactions]);

  /* ── KPI de conciliação ── */
  const concilKpis = useMemo(() => {
    const r = { conciliado: 0, confirmado: 0, pendente: 0 };
    filteredTransactions.forEach(tx => r[getConcilStatus(tx)]++);
    return r;
  }, [filteredTransactions]);

  /* ── Handlers ── */
  const handleFilterChange = (e) => {
    const { name, value } = e.target;
    setFilters(prev => ({ ...prev, [name]: value }));
  };

  const handleOpenModal = (mode, transaction = null) => {
    setModalMode(mode);
    if (transaction) {
      setFormData({
        id:           transaction.id,
        tipo:         transaction.tipo,
        descricao:    transaction.descricao,
        valor:        Math.abs(transaction.valor).toString(),
        data:         extractDatePart(transaction.data) || new Date().toISOString().split('T')[0],
        categoria_id: transaction.categoria_id || '',
      });
    } else {
      setFormData({
        id: null, tipo: 'Despesa', descricao: '',
        valor: '', data: new Date().toISOString().split('T')[0], categoria_id: '',
      });
    }
    setIsModalOpen(true);
  };

  const handleSave = async (e) => {
    e.preventDefault();
    try {
      const valorFormatado = parseFloat(formData.valor);
      const payload = {
        ...formData,
        valor: formData.tipo === 'Despesa'
          ? -Math.abs(valorFormatado)
          :  Math.abs(valorFormatado),
      };
      if (modalMode === 'create') await api.post('/financeiro',               payload);
      else                         await api.put(`/financeiro/${formData.id}`, payload);
      setIsModalOpen(false);
      fetchTransactions();
    } catch (error) {
      console.error('Erro ao salvar lançamento:', error);
      alert('Ocorreu um erro ao salvar o registro financeiro.');
    }
  };

  const handleDelete = async (id) => {
    if (window.confirm('Confirma o estorno deste lançamento? As contas vinculadas serão reabertas.')) {
      try {
        await api.delete(`/financeiro/${id}`);
        fetchTransactions();
      } catch (error) {
        alert(error.response?.data?.error || 'Erro crítico ao estornar a transação.');
      }
    }
  };

  /* ─── Render ─────────────────────────────────────────────────── */
  return (
    <div className="ex-fin-layout-wrapper">

      {/* HEADER */}
      <header className="ex-fin-topbar">
        <div className="ex-fin-title-area">
          <button className="ex-fin-back-btn" aria-label="Voltar">
            <ArrowLeft size={20} />
          </button>
          <div className="ex-fin-title-text">
            <h1>Extrato Consolidado</h1>
            <p>Visão geral de entradas, saídas e conciliações</p>
          </div>
        </div>
        <button className="ex-fin-action-primary" onClick={() => handleOpenModal('create')}>
          <Plus size={18} />
          <span>Novo Lançamento</span>
        </button>
      </header>

      {/* KPIs FINANCEIROS */}
      <section className="ex-fin-dashboard-grid">
        <div className="ex-fin-metric-box ex-fin-metric-income">
          <div className="ex-fin-metric-icon"><TrendingUp size={24} /></div>
          <div className="ex-fin-metric-data">
            <span>Receitas Operacionais</span>
            <strong>{formatCurrency(kpis.receitas)}</strong>
          </div>
        </div>
        <div className="ex-fin-metric-box ex-fin-metric-expense">
          <div className="ex-fin-metric-icon"><TrendingDown size={24} /></div>
          <div className="ex-fin-metric-data">
            <span>Despesas Operacionais</span>
            <strong>{formatCurrency(kpis.despesas)}</strong>
          </div>
        </div>
        <div className="ex-fin-metric-box ex-fin-metric-balance">
          <div className="ex-fin-metric-icon"><DollarSign size={24} /></div>
          <div className="ex-fin-metric-data">
            <span>Saldo do Período</span>
            <strong className={kpis.saldo < 0 ? 'ex-fin-text-danger' : ''}>
              {formatCurrency(kpis.saldo)}
            </strong>
          </div>
        </div>
      </section>

      {/* BARRA DE CONCILIAÇÃO */}
      <div className="ex-fin-concil-bar">
        <span className="ex-fin-concil-label">Status de Conciliação:</span>
        <span className="ex-fin-concil-chip chip-success">
          <CheckCircle size={13} /> {concilKpis.conciliado} Conciliados
        </span>
        <span className="ex-fin-concil-chip chip-info">
          <Shield size={13} /> {concilKpis.confirmado} Confirmados
        </span>
        <span className="ex-fin-concil-chip chip-warning">
          <Clock size={13} /> {concilKpis.pendente} Ag. Conciliação
        </span>
        {concilKpis.pendente > 0 && (
          <span className="ex-fin-concil-hint">
            ⚠️ {concilKpis.pendente} lançamento(s) sem origem identificada — verifique na Conciliação Bancária
          </span>
        )}
      </div>

      {/* FILTROS */}
      <div className="ex-fin-filter-bar">
        <div className="ex-fin-search-group">
          <Search className="ex-fin-icon-search" />
          <input
            type="text"
            placeholder="Buscar por descrição, categoria..."
            name="search"
            value={filters.search}
            onChange={handleFilterChange}
            className="ex-fin-input-search"
          />
        </div>
        <div className="ex-fin-date-group">
          <div className="ex-fin-input-wrapper">
            <label>Data Inicial</label>
            <input type="date" name="dataInicio" value={filters.dataInicio} onChange={handleFilterChange} />
          </div>
          <div className="ex-fin-input-wrapper">
            <label>Data Final</label>
            <input type="date" name="dataFim" value={filters.dataFim} onChange={handleFilterChange} />
          </div>
          <button className="ex-fin-btn-reload" onClick={fetchTransactions} title="Atualizar">
            <RefreshCw size={18} className={isLoading ? 'ex-fin-spin' : ''} />
          </button>
        </div>
      </div>

      {/* LISTA */}
      <main className="ex-fin-table-container">
        {isLoading ? (
          <div className="ex-fin-empty-state">Processando lançamentos do servidor...</div>
        ) : filteredTransactions.length === 0 ? (
          <div className="ex-fin-empty-state">
            Nenhum registro contábil encontrado para o filtro aplicado.
          </div>
        ) : (
          filteredTransactions.map(tx => {
            const dateObj   = parseDateForCard(tx.data);
            const isReceita = tx.tipo === 'Receita' || parseFloat(tx.valor) > 0;
            const { descricaoLimpa } = parseOSTarefa(tx.descricao);

            return (
              <div className="ex-fin-row" key={tx.id}>

                <div className="ex-fin-cell-date">
                  <span className="ex-fin-day">{dateObj.day}</span>
                  <span className="ex-fin-month">{dateObj.month}</span>
                </div>

                <div className="ex-fin-cell-info">
                  <div className="ex-fin-info-top">
                    <span className="ex-fin-desc" title={tx.descricao}>{descricaoLimpa}</span>
                    <span className={`ex-fin-pill ${isReceita ? 'ex-fin-pill-green' : 'ex-fin-pill-red'}`}>
                      {isReceita ? 'Entrada' : 'Saída'}
                    </span>
                  </div>
                  <div className="ex-fin-info-bottom">
                    {tx.categoria_nome && (
                      <span className="ex-fin-badge-gray">{tx.categoria_nome}</span>
                    )}
                    {/* Badge de origem: OS de Tarefa */}
                    <TarefaBadge tx={tx} />
                    {/* Badge de conciliação com lógica correta */}
                    <ConcilBadge tx={tx} />
                  </div>
                </div>

                <div className={`ex-fin-cell-amount ${isReceita ? 'ex-fin-amount-pos' : 'ex-fin-amount-neg'}`}>
                  {isReceita ? '+ ' : '- '}
                  {formatCurrency(Math.abs(parseFloat(tx.valor)))}
                </div>

                <div className="ex-fin-cell-actions">
                  <button
                    className="ex-fin-btn-icon ex-fin-icon-edit"
                    title="Editar"
                    onClick={() => handleOpenModal('edit', tx)}
                  >
                    <Edit2 size={16} />
                  </button>
                  <button
                    className="ex-fin-btn-icon ex-fin-icon-delete"
                    title="Estornar/Excluir"
                    onClick={() => handleDelete(tx.id)}
                  >
                    <Trash2 size={16} />
                  </button>
                </div>
              </div>
            );
          })
        )}
      </main>

      {/* MODAL */}
      {isModalOpen && (
        <div className="ex-fin-modal-backdrop">
          <div className="ex-fin-modal-panel">
            <div className="ex-fin-modal-head">
              <h3>{modalMode === 'create' ? 'Novo Lançamento Manual' : 'Editar Lançamento'}</h3>
              <button className="ex-fin-modal-close" onClick={() => setIsModalOpen(false)}>
                <X size={24} />
              </button>
            </div>
            <form onSubmit={handleSave}>
              <div className="ex-fin-modal-body">
                <div className="ex-fin-toggle-group">
                  <button
                    type="button"
                    className={formData.tipo === 'Receita' ? 'ex-fin-toggle-on-green' : 'ex-fin-toggle-off'}
                    onClick={() => setFormData({ ...formData, tipo: 'Receita' })}
                  >Entrada</button>
                  <button
                    type="button"
                    className={formData.tipo === 'Despesa' ? 'ex-fin-toggle-on-red' : 'ex-fin-toggle-off'}
                    onClick={() => setFormData({ ...formData, tipo: 'Despesa' })}
                  >Saída</button>
                </div>
                <div className="ex-fin-field" style={{ marginTop: '10px' }}>
                  <label>Valor (R$)</label>
                  <input
                    type="number" step="0.01" name="valor"
                    className="ex-fin-input-massive" placeholder="0.00" required
                    value={formData.valor}
                    onChange={(e) => setFormData({ ...formData, valor: e.target.value })}
                  />
                </div>
                <div className="ex-fin-field">
                  <label>Descrição / Histórico</label>
                  <input
                    type="text" name="descricao"
                    className="ex-fin-input-std" placeholder="Ex: Pagamento Fornecedor X" required
                    value={formData.descricao}
                    onChange={(e) => setFormData({ ...formData, descricao: e.target.value })}
                  />
                </div>
                <div className="ex-fin-grid-2">
                  <div className="ex-fin-field">
                    <label>Data de Competência</label>
                    <div className="ex-fin-relative-input">
                      <Calendar size={16} className="ex-fin-icon-inside" />
                      <input
                        type="date" name="data" required
                        className="ex-fin-input-std ex-fin-pl-35"
                        value={formData.data}
                        onChange={(e) => setFormData({ ...formData, data: e.target.value })}
                      />
                    </div>
                  </div>
                  <div className="ex-fin-field">
                    <label>Categoria Contábil (ID)</label>
                    <input
                      type="number" name="categoria_id"
                      className="ex-fin-input-std" placeholder="ID da Categoria"
                      value={formData.categoria_id}
                      onChange={(e) => setFormData({ ...formData, categoria_id: e.target.value })}
                    />
                  </div>
                </div>
              </div>
              <div className="ex-fin-modal-foot">
                <button type="button" className="ex-fin-btn-ghost" onClick={() => setIsModalOpen(false)}>
                  Cancelar
                </button>
                <button type="submit" className="ex-fin-btn-confirm">
                  <CheckCircle size={18} /> Salvar Lançamento
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};

export default ExtratoFinanceiro;